import { useState } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  IconButton,
  Paper,
} from "@mui/material";
import {
  Dashboard,
  People,
  Category,
  Groups,
  ContactMail,
  Campaign,
  Logout,
  Settings,
  Menu as MenuIcon,
} from "@mui/icons-material";
import { logout } from "../utils/auth";

const drawerWidth = 260;
const collapsedWidth = 72;

const Layout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(true);
  const [hoveredItem, setHoveredItem] = useState(null);

  const toggleDrawer = () => {
    setDrawerOpen((prev) => !prev);
  };

  const navItems = [
    { path: "/dashboard", label: "Dashboard", icon: <Dashboard /> },
    { path: "/users", label: "Users", icon: <People /> },
    { path: "/categories", label: "Categories", icon: <Category /> },
    {
      path: "/committees",
      label: "Committees",
      icon: <Groups />,
      match: (p) =>
        p === "/committees" ||
        p.startsWith("/committees/") ||
        p.startsWith("/committee") ||
        p.startsWith("/add-committee") ||
        p.startsWith("/edit-committee"),
    },
    { path: "/contacts", label: "Contacts", icon: <ContactMail /> },
    { path: "/notifications", label: "Notifications", icon: <Campaign /> },
  ];

  const isSelected = (item) => {
    if (item.match) return item.match(location.pathname);
    return location.pathname === item.path;
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "background.default" }}>
      <Drawer
        variant="permanent"
        sx={{
          width: drawerOpen ? drawerWidth : collapsedWidth,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: drawerOpen ? drawerWidth : collapsedWidth,
            transition: "width 0.25s ease-in-out",
            overflowX: "hidden",
            borderRight: "1px solid",
            borderColor: "divider",
            boxSizing: "border-box",
            bgcolor: "background.paper",
          },
        }}
      >
        <Toolbar
          sx={{
            minHeight: { xs: 56 },
            justifyContent: "flex-start",
            px: 1.5,
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <IconButton
            onClick={toggleDrawer}
            size="medium"
            sx={{
              color: "primary.main",
              "&:hover": { bgcolor: "rgba(94, 53, 177, 0.08)" },
            }}
          >
            <MenuIcon />
          </IconButton>
          {drawerOpen && (
            <Typography
              variant="subtitle1"
              fontWeight={700}
              color="primary.main"
              sx={{ ml: 1.5 }}
            >
              Treasure
            </Typography>
          )}
        </Toolbar>

        <List sx={{ px: 1, py: 2 }}>
          {navItems.map((item) => (
            <ListItemButton
              key={item.path}
              selected={isSelected(item)}
              onClick={() => navigate(item.path)}
              sx={{
                mb: 0.5,
                borderRadius: 2,
                py: 1.25,
                "& .MuiListItemIcon-root": {
                      minWidth: drawerOpen ? 40 : 0,
                    },
              }}
            >
              <ListItemIcon sx={{ color: isSelected(item) ? "primary.main" : "action.active" }}>
                {item.icon}
              </ListItemIcon>
              {drawerOpen && (
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    fontWeight: isSelected(item) ? 600 : 500,
                    fontSize: "0.9375rem",
                  }}
                />
              )}
            </ListItemButton>
          ))}
          <ListItemButton
            onMouseEnter={() => setHoveredItem("settings")}
            onMouseLeave={() => setHoveredItem(null)}
            sx={{
              mb: 0.5,
              borderRadius: 2,
              py: 1.25,
              "& .MuiListItemIcon-root": { minWidth: drawerOpen ? 40 : 0 },
            }}
          >
            <ListItemIcon sx={{ color: "action.active" }}>
              <Settings />
            </ListItemIcon>
            {drawerOpen && <ListItemText primary="Settings" primaryTypographyProps={{ fontSize: "0.9375rem" }} />}
          </ListItemButton>
        </List>
      </Drawer>

      {!drawerOpen && hoveredItem === "settings" && (
        <Paper
          elevation={4}
          onMouseEnter={() => setHoveredItem("settings")}
          onMouseLeave={() => setHoveredItem(null)}
          sx={{
            position: "fixed",
            left: collapsedWidth,
            top: 320,
            zIndex: 1300,
            width: 200,
            borderRadius: 2,
            overflow: "hidden",
          }}
        >
          <List dense>
            <ListItemButton onClick={() => alert("Profile")}>
              <ListItemText primary="Profile" />
            </ListItemButton>
            <ListItemButton onClick={() => alert("Preferences")}>
              <ListItemText primary="Preferences" />
            </ListItemButton>
          </List>
        </Paper>
      )}

      <Box component="main" sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}>
        <AppBar
          position="fixed"
          elevation={0}
          sx={{
            width: `calc(100% - ${drawerOpen ? drawerWidth : collapsedWidth}px)`,
            ml: `${drawerOpen ? drawerWidth : collapsedWidth}px`,
            transition: "width 0.25s ease-in-out, margin 0.25s ease-in-out",
            bgcolor: "primary.main",
            background: "linear-gradient(135deg, #5E35B1 0%, #7E57C2 100%)",
          }}
        >
          <Toolbar sx={{ justifyContent: "space-between", px: { xs: 2, sm: 3 } }}>
            <Typography variant="h6" fontWeight={600} color="inherit">
              Admin Panel
            </Typography>
            <Button
              color="inherit"
              onClick={logout}
              startIcon={<Logout sx={{ fontSize: 20 }} />}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                "&:hover": { bgcolor: "rgba(255,255,255,0.12)" },
                borderRadius: 2,
              }}
            >
              Logout
            </Button>
          </Toolbar>
        </AppBar>

        <Toolbar />
        <Box sx={{ flex: 1, bgcolor: "background.default" }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
};

export default Layout;
